/**
 * VoiceChat Component
 * WebRTC voice chat interface with OpenAI Realtime API
 */

import React from 'react';
import { FaMicrophone, FaStop, FaSpinner, FaPaperPlane } from 'react-icons/fa';
import useVoiceChat from '../hooks/useVoiceChat';
import { VOICE_CHAT_ENABLED } from '../config/chatConfig';
import '../styles/VoiceChat.css';

const VoiceChat = React.forwardRef(({
  user,
  onTranscriptUpdate,
  onVoiceSessionChange,
  currentSessionId,
  navigate,
  refreshSessions,
  className = '',
  size = 12
}, ref) => {
  const {
    isConnected,
    isConnecting,
    isUserSpeaking, // eslint-disable-line no-unused-vars
    isAISpeaking, // eslint-disable-line no-unused-vars
    error,
    errorDetails,
    requestSent,
    transcript,
    toggleVoiceChat,
    clearError,
    requestQuotaIncrease,
    isRecording
  } = useVoiceChat(user, currentSessionId, navigate, refreshSessions);

  // Expose toggleVoiceChat to parent (must be called before any early returns)
  React.useImperativeHandle(ref, () => ({
    toggleVoiceChat
  }));

  // Report voice session state to parent
  React.useEffect(() => {
    if (onVoiceSessionChange) {
      onVoiceSessionChange(isConnected || isConnecting);
    }
  }, [isConnected, isConnecting, onVoiceSessionChange]);

  // Track which messages have been sent to avoid duplicates
  const sentMessagesRef = React.useRef(new Set());

  // Handle transcript updates - only send new messages
  React.useEffect(() => {
    // Don't send messages during disconnect process
    if (!isConnected && !isConnecting) {
      return;
    }
    
    if (transcript.length > 0 && onTranscriptUpdate) {
      transcript.forEach((message) => {
        // Skip null/undefined messages
        if (!message || !message.role || !message.text) {
          // console.warn('⚠️ Skipping invalid message:', message);
          return;
        }
        
        // Create unique key for message
        const messageKey = `${message.role}-${message.timestamp}-${message.text.substring(0, 30)}`;
        
        // Only send if not already sent
        if (!sentMessagesRef.current.has(messageKey)) {
          sentMessagesRef.current.add(messageKey);
          onTranscriptUpdate(message);
        }
      });
    }
  }, [transcript, onTranscriptUpdate, isConnected, isConnecting]);

  // Clear sent messages tracking when disconnected
  React.useEffect(() => {
    if (!isConnected && !isConnecting) {
      sentMessagesRef.current.clear();
    }
  }, [isConnected, isConnecting]);

  // Check if user has access to voice chat based on tier
  // Allow all authenticated users (all tiers: standard, pro, advanced, admin)
  const hasVoiceChatAccess = VOICE_CHAT_ENABLED && user;
  
  // Don't render voice chat button if user doesn't have access
  if (!hasVoiceChatAccess) {
    // Return regular send button for non-admin users (fallback to text-only mode)
    return (
      <FaPaperPlane 
        size={size} 
        style={{ 
          width: `${size}px`, 
          height: `${size}px`
        }} 
      />
    );
  }

  // Simple state-based rendering
  let icon;
  if (isConnecting) {
    icon = <FaSpinner className="voice-spinner" size={size} style={{ width: `${size}px`, height: `${size}px`, pointerEvents: 'none' }} />;
  } else if (isConnected) {
    icon = <FaStop size={size} style={{ width: `${size}px`, height: `${size}px`, color: '#ff4444', pointerEvents: 'none' }} />;
  } else {
    icon = (
      <FaMicrophone
        size={size}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          color: (isRecording || error) ? '#ff4444' : 'inherit',
          pointerEvents: 'none'
        }}
      />
    );
  }

  return (
    <>
      {icon}
      {errorDetails && (
        <VoiceErrorPopup
          details={errorDetails}
          requestSent={requestSent}
          onRequest={requestQuotaIncrease}
          onClose={clearError}
        />
      )}
    </>
  );
});

function VoiceErrorPopup({ details, requestSent, onRequest, onClose }) {
  const isQuota = details.code === 'quota_exceeded';
  const title = isQuota ? 'Daily voice quota used' : 'Voice unavailable';
  const body = isQuota
    ? `You've used all ${details.quotaLimit ?? ''} minutes for today. Resets at ${details.resetTime || '1:00 AM UTC'}${details.resetDate ? ` on ${details.resetDate}` : ''}.`
    : (details.message || 'Could not start voice chat.');

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999, pointerEvents: 'auto'
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface, #1f1f1f)', color: 'var(--text, #fff)',
          padding: '20px 22px', borderRadius: 10, maxWidth: 360, width: '90%',
          boxShadow: '0 10px 30px rgba(0,0,0,0.4)', fontSize: 14, lineHeight: 1.45
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 8 }}>{title}</div>
        <div style={{ marginBottom: 16, opacity: 0.85 }}>{body}</div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          {!requestSent ? (
            <button
              type="button"
              onClick={onRequest}
              style={{
                background: '#7c5cff', color: '#fff', border: 0,
                padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13
              }}
            >
              Request more
            </button>
          ) : (
            <span style={{ fontSize: 13, opacity: 0.85, alignSelf: 'center' }}>
              Request sent
            </span>
          )}
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'transparent', color: 'inherit',
              border: '1px solid rgba(255,255,255,0.25)',
              padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default VoiceChat;